/**
 * Defer transient TradeStation stream error banners until reconnect retries are exhausted.
 * Matches server StreamConnection retry window (~45s initial data + reconnect backoff).
 */
export const STREAM_ERROR_BANNER_GRACE_MS = 45_000;

const pending = new Map();

/** Inline TS quote/position enrichment failures while the stream may still recover. */
export function isTransientBrokerStreamError(data) {
  if (!data?.Error && data?.Message == null) return false;
  const msg = String(data.Message || data.Error || '');
  if (/retrieve quotes for position/i.test(msg)) return true;
  if (data.Symbol && data.Error && !/stream connection failed/i.test(msg)) return true;
  return false;
}

/** Permanent upstream failure (server exhausted reconnect attempts). */
export function isPermanentStreamFailure(data) {
  if (!data?.Error) return false;
  const err = String(data.Error);
  const msg = String(data.Message || '').toLowerCase();
  if (['Unauthorized', 'Forbidden', 'UpstreamError', 'Unknown'].includes(err)) return true;
  if (msg.includes('stream connection failed')) return true;
  if (err === 'DualLogon') return true;
  return false;
}

/**
 * Internal broker/hub noise that must never reach the user banner
 * (e.g. CT "already a subscription" races, raw `[CT-MD]` log lines).
 */
export function shouldSuppressStreamErrorBanner(message) {
  const msg = String(message || '');
  if (!msg) return true;
  if (/\[CT-(MD|stream)\]/i.test(msg)) return true;
  if (/already a subscription/i.test(msg)) return true;
  // Multi-broker: capability 501s for a non-MD active row must not toast while
  // the selected MD account's chart/REST still works.
  if (/not available for (this|your) broker connection/i.test(msg)) return true;
  if (/require market data on your broker connection/i.test(msg)) return true;
  if (/BROKER_ENDPOINT_UNSUPPORTED/i.test(msg)) return true;
  return false;
}

/**
 * @returns {boolean} true when the banner should be delayed (or suppressed during WS reconnect).
 */
export function shouldDeferStreamErrorBanner(data, socketReady) {
  if (!socketReady) return true;
  if (isPermanentStreamFailure(data)) return false;
  if (isTransientBrokerStreamError(data)) return true;
  return false;
}

export function clearStreamErrorBanner(streamKey) {
  const entry = pending.get(streamKey);
  if (entry?.timer) clearTimeout(entry.timer);
  pending.delete(streamKey);
}

export function clearAllStreamErrorBanners() {
  for (const key of [...pending.keys()]) {
    clearStreamErrorBanner(key);
  }
}

/**
 * Queue or show a stream error banner.
 * @param {object} opts
 * @param {string} opts.streamKey - e.g. 'quotes', 'positions', 'bars'
 * @param {string} opts.message
 * @param {Function} opts.showMessage - BannerContext showMessage
 * @param {() => boolean} [opts.isHealthy] - if true when timer fires, banner is skipped
 * @param {boolean} [opts.defer]
 * @param {number} [opts.graceMs]
 * @param {string} [opts.alertType]
 * @param {string} [opts.mode]
 */
export function reportStreamError({
  streamKey,
  message,
  showMessage,
  isHealthy,
  defer = false,
  graceMs = STREAM_ERROR_BANNER_GRACE_MS,
  alertType = 'danger',
  mode = 'notification',
}) {
  if (!message || !showMessage || !streamKey) return;
  if (shouldSuppressStreamErrorBanner(message)) return;

  clearStreamErrorBanner(streamKey);

  if (!defer) {
    showMessage(message, alertType, true, mode);
    return;
  }

  const timer = setTimeout(() => {
    pending.delete(streamKey);
    if (isHealthy?.()) return;
    showMessage(message, alertType, true, mode);
  }, graceMs);

  pending.set(streamKey, { timer, message });
}

export function onStreamRecovered(streamKey) {
  clearStreamErrorBanner(streamKey);
}
